import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BarChart3, TrendingUp, ArrowUpRight } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import { BentoCard } from "@/components/dashboard/BentoCard";
import { DASHBOARD } from "@/constants/testIds";

/** Formats "2026-05" → "May 26" for compact bar labels. */
const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
function fmtMonth(ym) {
  const [y, m] = (ym || "").split("-");
  const idx = Math.max(1, Math.min(12, parseInt(m, 10) || 0)) - 1;
  return `${MONTH_ABBR[idx] || m} ${(y || "").slice(2)}`;
}

export function DashboardAnalytics() {
  const { data, isLoading } = useQuery({
    queryKey: ["stats-analytics"],
    queryFn: async () => (await api.get("/stats/analytics")).data,
    refetchInterval: 60000,
  });

  const histogram = data?.sale_date_histogram || [];
  const topCounties = data?.top_counties || [];
  const maxCount = histogram.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
  const maxOwed = topCounties.reduce((m, r) => Math.max(m, r.total_owed || 0), 0) || 1;

  return (
    <div
      data-testid={DASHBOARD.analyticsCard}
      className="grid grid-cols-1 lg:grid-cols-3 gap-4"
    >
      {/* Sale-date histogram */}
      <BentoCard
        data-testid={DASHBOARD.histogramCard}
        className="lg:col-span-2"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} strokeWidth={2.5} className="text-red-600" />
            <span className="data-label">Upcoming sales by month</span>
          </div>
          <Link
            to="/properties"
            className="inline-flex items-center gap-1 text-xs font-semibold underline decoration-yellow-500 decoration-2 underline-offset-4 hover:text-yellow-700"
          >
            Browse <ArrowUpRight size={12} />
          </Link>
        </div>
        {isLoading ? (
          <p className="mt-8 text-sm text-zinc-500">Loading analytics…</p>
        ) : histogram.length === 0 ? (
          <p className="mt-8 text-sm text-zinc-500">
            No dated sales yet. Run a scrape to populate the histogram.
          </p>
        ) : (
          <div className="mt-6 flex items-end gap-2 h-40">
            {histogram.map((row) => {
              const h = Math.round((row.count / maxCount) * 100);
              return (
                <div
                  key={row.month}
                  data-testid={`hist-bar-${row.month}`}
                  className="flex-1 flex flex-col items-center gap-1 min-w-0"
                  title={`${row.month}: ${row.count} sales`}
                >
                  <div
                    className="w-full border-2 border-black bg-yellow-400"
                    style={{ height: `${Math.max(4, h)}%` }}
                    aria-label={`${row.count} sales in ${row.month}`}
                  />
                  <div className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-600 truncate w-full text-center">
                    {fmtMonth(row.month)}
                  </div>
                  <div className="font-display font-bold text-xs">{row.count}</div>
                </div>
              );
            })}
          </div>
        )}
      </BentoCard>

      {/* Top counties by $ owed */}
      <BentoCard data-testid={DASHBOARD.topCountiesCard}>
        <div className="flex items-center gap-2">
          <TrendingUp size={16} strokeWidth={2.5} className="text-red-600" />
          <span className="data-label">Top counties by $ owed</span>
        </div>
        {isLoading ? (
          <p className="mt-6 text-sm text-zinc-500">Loading…</p>
        ) : topCounties.length === 0 ? (
          <p className="mt-6 text-sm text-zinc-500">
            No delinquency data yet — trigger a scrape to fill this in.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {topCounties.map((row) => {
              const pct = Math.round((row.total_owed / maxOwed) * 100);
              return (
                <li
                  key={`${row.jurisdiction}-${row.state}`}
                  data-testid={`county-row-${row.jurisdiction.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-display font-semibold text-sm truncate">
                      {row.jurisdiction}
                      <span className="ml-1 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                        {row.state}
                      </span>
                    </span>
                    <span className="font-mono-data text-xs whitespace-nowrap">
                      {fmtMoney(row.total_owed)}
                    </span>
                  </div>
                  <div className="mt-1 h-2 border border-black bg-white">
                    <div
                      className="h-full bg-red-500"
                      style={{ width: `${Math.max(3, pct)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </BentoCard>
    </div>
  );
}
